// Which run this tab is following. The run itself lives on the server (tailor-run).
// Chrome can discard a background tab; sessionStorage survives that reload, so the
// page reattaches to the same run and the timer continues from the original start.

import { holdTabAwake, releaseTab } from './keepTabAlive';

const RUN_KEY = 'tw-active-run';
const FORM_KEY = 'resume-tailoring-claude-direct-state';
const HANDOFF_KEY = 'tw-run-handoff';
const MAX_RUN_AGE_MS = 45 * 60_000;

export interface ActiveRun { runId: string; startTs: number; autoResumes?: number; failed?: boolean }

// A resume only reattaches to the server run, so it costs nothing. The limit stops
// a page stuck reloading from spinning forever.
const MAX_AUTO_RESUMES = 5;

function djb2(s: string): string {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}

function storedRun(): ActiveRun | null {
  try {
    const raw = sessionStorage.getItem(RUN_KEY);
    if (!raw) return null;
    const run = JSON.parse(raw) as ActiveRun;
    if (!run?.runId || !run?.startTs || Date.now() - run.startTs > MAX_RUN_AGE_MS) return null;
    return run;
  } catch { return null; }
}

function saveRun(run: ActiveRun): void {
  try { sessionStorage.setItem(RUN_KEY, JSON.stringify(run)); } catch { /* quota/private mode: run just is not resumable */ }
}

/** Keeps a copy outside this tab, so a new tab can pick the run up if this one is closed. */
export function mirrorRunForHandoff(): void {
  try {
    const run = sessionStorage.getItem(RUN_KEY);
    if (!run) return;
    localStorage.setItem(HANDOFF_KEY, JSON.stringify({ run, form: sessionStorage.getItem(FORM_KEY) }));
  } catch { /* quota/private mode: a closed tab just cannot be picked up */ }
}

/**
 * Runs once before the app starts. A fresh tab takes over a run whose tab was closed.
 * A tab still following a run holds a lock, and two followers would charge twice.
 */
export async function takeOverClosedRun(): Promise<void> {
  try {
    if (sessionStorage.getItem(RUN_KEY)) return;
    const raw = localStorage.getItem(HANDOFF_KEY);
    if (!raw) return;
    const held = (await navigator.locks?.query())?.held ?? [];
    if (held.some((l) => l.name?.startsWith('tw-run-'))) return;
    const { run, form } = JSON.parse(raw) as { run: string; form: string | null };
    localStorage.removeItem(HANDOFF_KEY);
    const parsed = JSON.parse(run) as ActiveRun;
    if (parsed.failed || Date.now() - parsed.startTs > MAX_RUN_AGE_MS) return;
    sessionStorage.setItem(RUN_KEY, run);
    if (form) sessionStorage.setItem(FORM_KEY, form);
  } catch { /* nothing to take over */ }
}

/** The run a reload interrupted. A failed run is over: it never comes back by itself. */
export function getActiveRun(): ActiveRun | null {
  const run = storedRun();
  return run && !run.failed ? run : null;
}

/** Counts an automatic resume. False means the limit is reached and the run must stop. */
export function claimAutoResume(): boolean {
  const run = storedRun();
  if (!run) return false;
  const used = run.autoResumes ?? 0;
  if (used >= MAX_AUTO_RESUMES) return false;
  saveRun({ ...run, autoResumes: used + 1 });
  return true;
}

/** Stops following the run. The server keeps its finished steps, so starting the same job again reuses them. */
export function markRunFailed(): void {
  const run = storedRun();
  if (run) saveRun({ ...run, failed: true });
  try { localStorage.removeItem(HANDOFF_KEY); } catch { /* nothing to clean */ }
}

/** Start a run, or pick up the one a reload interrupted. */
export function beginOrResumeRun(): { run: ActiveRun; resumed: boolean } {
  holdTabAwake();
  const existing = storedRun();
  if (existing && !existing.failed) return { run: existing, resumed: true };
  // Same id after a failure: the server reuses the steps it already finished.
  const run: ActiveRun = { runId: existing?.runId ?? Math.random().toString(36).slice(2, 10), startTs: Date.now() };
  try { sessionStorage.setItem(RUN_KEY, JSON.stringify(run)); } catch { /* quota/private mode: run just is not resumable */ }
  return { run, resumed: false };
}

/** The run finished (or the user cancelled): stop following it. */
export function endRun(): void {
  releaseTab();
  try { sessionStorage.removeItem(RUN_KEY); localStorage.removeItem(HANDOFF_KEY); } catch { /* nothing to clean */ }
}
